import React, { memo } from 'react';
import { Handle, Position, NodeToolbar } from '@xyflow/react';
import { NODE_TYPES } from './nodeTypes';
import { EXECUTION_STATUS_STYLES } from './executionStatus';

const ROUTE_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#10b981', '#0ea5e9', '#8b5cf6', '#ef4444', '#14b8a6'];

function WorkflowNode({ data, selected }) {
  const nodeType = NODE_TYPES[data.nodeType];
  if (!nodeType) return null;

  const Icon = nodeType.icon;
  const isTrigger = nodeType.category === 'trigger';
  const isConditional = data.nodeType === 'conditional';
  const isRouter = data.nodeType === 'router';
  const isTerminal = data.nodeType === 'stop';
  const execStatus = data.executionStatus;
  const statusStyle = execStatus ? EXECUTION_STATUS_STYLES[execStatus] : null;

  const categoryLabel = isTrigger ? 'Trigger' : (nodeType.subcategory || 'Action');

  // Router rules from config
  const routerRules = isRouter ? (data.routerRules || []) : [];
  const routerHandleCount = routerRules.length + 1; // +1 for default

  return (
    <>
      <NodeToolbar isVisible position={Position.Top} offset={4}>
        <div className="text-[10px] font-medium text-gray-400 tracking-wide pointer-events-none select-none">
          {categoryLabel}
        </div>
      </NodeToolbar>

      <div
        className={`relative bg-white rounded-xl border min-w-[160px] transition-all duration-200 ${
          data.highlight
            ? 'ring-2 ring-amber-300 border-amber-300 shadow-sm'
            : selected
            ? 'ring-1 ring-gray-400 border-gray-400 shadow-sm'
            : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
        }`}
      >
        {/* Execution status badge */}
        {statusStyle && statusStyle.badge}

        {/* Target handle (not on triggers) */}
        {!isTrigger && (
          <Handle
            type="target"
            position={Position.Left}
            className="!w-2 !h-2 !bg-gray-300 !border-2 !border-white"
          />
        )}

        {/* Node content */}
        <div className="px-3 py-2">
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${nodeType.color}12` }}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: nodeType.color }} strokeWidth={2} />
            </div>
            <span className="text-[13px] font-medium text-gray-800 truncate">
              {data.label || nodeType.label}
            </span>
          </div>
        </div>

        {/* Source handles */}
        {isTerminal ? null : isRouter ? (
          <>
            {routerRules.map((rule, idx) => {
              const pct = ((idx + 1) / (routerHandleCount + 1)) * 100;
              const color = ROUTE_COLORS[idx % ROUTE_COLORS.length];
              return (
                <Handle
                  key={`route-${idx}`}
                  type="source"
                  position={Position.Right}
                  id={`route-${idx}`}
                  className="!w-2 !h-2 !border-2 !border-white"
                  style={{ top: `${pct}%`, backgroundColor: color }}
                />
              );
            })}
            {/* Default handle */}
            <Handle
              type="source"
              position={Position.Right}
              id="default"
              className="!w-2 !h-2 !bg-gray-400 !border-2 !border-white"
              style={{ top: `${((routerHandleCount) / (routerHandleCount + 1)) * 100}%` }}
            />
            {/* Labels */}
            <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-around pr-4 pointer-events-none" style={{ paddingTop: '4px', paddingBottom: '4px' }}>
              {routerRules.map((rule, idx) => (
                <span key={idx} className="text-[8px] font-medium truncate max-w-[60px]" style={{ color: ROUTE_COLORS[idx % ROUTE_COLORS.length] }}>
                  {rule.label || `R${idx + 1}`}
                </span>
              ))}
              <span className="text-[8px] text-gray-400 font-medium">Default</span>
            </div>
          </>
        ) : isConditional ? (
          <>
            <Handle
              type="source"
              position={Position.Right}
              id="true"
              className="!w-2 !h-2 !bg-emerald-400 !border-2 !border-white"
              style={{ top: '30%' }}
            />
            <Handle
              type="source"
              position={Position.Right}
              id="false"
              className="!w-2 !h-2 !bg-red-400 !border-2 !border-white"
              style={{ top: '70%' }}
            />
            <div className="absolute right-0 top-0 bottom-0 flex flex-col justify-around pr-4 pointer-events-none">
              <span className="text-[9px] text-emerald-600 font-medium">T</span>
              <span className="text-[9px] text-red-500 font-medium">F</span>
            </div>
          </>
        ) : (
          <Handle
            type="source"
            position={Position.Right}
            className="!w-2 !h-2 !bg-gray-300 !border-2 !border-white"
          />
        )}
      </div>
    </>
  );
}

export default memo(WorkflowNode);
